import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { ArrowDown, ArrowUp, MessageSquare, Loader2, Send } from "lucide-react";
import { SmsComposeDialog } from "./SmsComposeDialog";

interface Message {
  id: string;
  direction: string;
  channel: string;
  body: string;
  created_at: string;
  status: string;
}

interface CustomerCommunicationTabProps {
  customerId: string;
  customerName: string;
  customerPhone: string | null;
}

export function CustomerCommunicationTab({ customerId, customerName, customerPhone }: CustomerCommunicationTabProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSmsDialogOpen, setIsSmsDialogOpen] = useState(false);

  useEffect(() => {
    loadMessages();
  }, [customerId]);

  const loadMessages = async () => {
    setIsLoading(true);
    const { data } = await supabase
      .from("messages")
      .select("id, direction, channel, body, created_at, status")
      .eq("customer_id", customerId)
      .order("created_at", { ascending: false })
      .limit(50);
    setMessages(data || []);
    setIsLoading(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <MessageSquare className="h-4 w-4" />
          Communication History
        </h4>
        <Button
          size="sm"
          className="h-7 text-xs"
          onClick={() => setIsSmsDialogOpen(true)}
          disabled={!customerPhone}
        >
          <Send className="h-3 w-3 mr-1" /> Send SMS
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-6 bg-muted/30 rounded-lg">
          <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-xs text-muted-foreground">No messages yet</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`p-2.5 rounded-lg text-xs border ${
                msg.direction === "inbound"
                  ? "bg-blue-50 border-blue-100"
                  : "bg-green-50 border-green-100"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  {msg.direction === "inbound" ? (
                    <ArrowDown className="h-3 w-3 text-blue-600" />
                  ) : (
                    <ArrowUp className="h-3 w-3 text-green-600" />
                  )}
                  <span className="font-semibold">
                    {msg.direction === "inbound" ? "Received" : "Sent"}
                  </span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                    {msg.channel.toUpperCase()}
                  </Badge>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                </span>
              </div>
              <p className="text-[11px] leading-relaxed break-words">{msg.body}</p>
            </div>
          ))}
        </div>
      )}

      <SmsComposeDialog
        open={isSmsDialogOpen}
        onOpenChange={setIsSmsDialogOpen}
        customerId={customerId}
        customerName={customerName}
        customerPhone={customerPhone}
        onSent={loadMessages}
      />
    </div>
  );
}
